import { DialogComponent } from '@theme/dialog';
import { CartAddingEvent, ThemeEvents } from '@theme/events';

let shouldAutoOpenCartDrawer = false;
let keepCartDrawerOpen = false;
/** @type {ReturnType<typeof setTimeout> | undefined} */
let clearLoadingTimeoutId;

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    this.setAttribute('disable-outside-close', '');
    this.setAttribute('disable-escape-close', '');
    this.addEventListener('dialog:open', this.#handleDialogOpen);
    this.refs.dialog?.addEventListener('click', this.#handleBackdropClick);
    document.addEventListener(CartAddingEvent.eventName, this.#handleCartAdding);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartSettled);
    document.addEventListener(ThemeEvents.cartError, this.#handleCartSettled);
    this.refs.dialog?.addEventListener('close', this.#handleNativeClose);
    this.refs.dialog?.addEventListener('cancel', this.#handleNativeCancel);

    // If section morph replaced the old drawer during an in-flight add,
    // immediately re-open this new instance with loader.
    if (this.hasAttribute('auto-open') && shouldAutoOpenCartDrawer) {
      keepCartDrawerOpen = true;
      this.refs.dialog?.setAttribute('loading', '');
      this.showDialog();
      this.#startLoadingFallbackTimer();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('dialog:open', this.#handleDialogOpen);
    document.removeEventListener(CartAddingEvent.eventName, this.#handleCartAdding);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartSettled);
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartSettled);
    this.refs.dialog?.removeEventListener('close', this.#handleNativeClose);
    this.refs.dialog?.removeEventListener('cancel', this.#handleNativeCancel);
    this.refs.dialog?.removeEventListener('click', this.#handleBackdropClick);
  }

  #handleDialogOpen = () => {
    document.dispatchEvent(new CustomEvent('cart-drawer:opened'));
  };

  #handleNativeCancel = () => {};

  #handleBackdropClick = (/** @type {MouseEvent} */ event) => {
    const dialog = this.refs.dialog;
    if (!dialog) return;
    const rect = dialog.getBoundingClientRect();
    const outsidePanel =
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;
    if (outsidePanel) {
      this.close();
    }
  };

  #handleNativeClose = () => {
    if (!keepCartDrawerOpen) return;

    // If dialog gets closed by any non-explicit path (e.g. section morph), reopen.
    // Explicit closes go through close() which clears keepCartDrawerOpen first,
    // so by the time the native 'close' event fires, keepCartDrawerOpen is false
    // and this handler exits early above.
    requestAnimationFrame(() => {
      const activeDrawer = document.querySelector('cart-drawer-component[auto-open]');
      const drawer = activeDrawer instanceof CartDrawerComponent ? activeDrawer : this;
      drawer.showDialog();
    });
  };

  #handleCartAdding = () => {
    if (!this.hasAttribute('auto-open')) return;

    shouldAutoOpenCartDrawer = true;
    keepCartDrawerOpen = true;

    // Open immediately before fetch completes and show loading overlay.
    this.refs.dialog?.setAttribute('loading', '');
    this.#startLoadingFallbackTimer();
    this.showDialog();
  };

  /** @param {Event} event */
  #handleCartSettled = (event) => {
    if (!this.hasAttribute('auto-open')) return;
    if (!shouldAutoOpenCartDrawer && !this.refs.dialog?.hasAttribute('loading')) return;

    this.#clearLoadingState(`cart settled: ${event.type}`);
  };

  #startLoadingFallbackTimer() {
    if (clearLoadingTimeoutId) {
      clearTimeout(clearLoadingTimeoutId);
    }

    // Safety: never leave drawer stuck in loading if upstream event does not fire.
    clearLoadingTimeoutId = setTimeout(() => {
      this.#clearLoadingState('loading fallback timeout');
    }, 8000);
  }

  /** @param {string} reason */
  #clearLoadingState(reason) {
    if (clearLoadingTimeoutId) {
      clearTimeout(clearLoadingTimeoutId);
      clearLoadingTimeoutId = undefined;
    }

    this.refs.dialog?.removeAttribute('loading');
    shouldAutoOpenCartDrawer = false;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    keepCartDrawerOpen = false;
    shouldAutoOpenCartDrawer = false;
    this.#clearLoadingState('explicit close()');
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
